import { projects, works } from "@folio-2026/content"
import Experience, { useExperienceStore } from "@folio-2026/experience"
import { AnimatedText, EVENTS, Grid, MQ } from "@folio-2026/shared"
import { useGSAP } from "@gsap/react"
import { gsap, ScrollTrigger } from "gsap/all"
import Snap from "lenis/snap"
import { useRef } from "react"

import useSectionReady from "../../../hooks/useSectionReady"

gsap.registerPlugin(ScrollTrigger)

export default function SectionWorks() {
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const experience = useRef(Experience.getInstance())
  const snapRef = useRef<InstanceType<typeof Snap> | null>(null)

  const isSectionReady = useSectionReady("Works")
  const { width } = useExperienceStore()
  const isMobile = width > 0 && width < MQ.sm

  useGSAP(
    ({ selector }) => {
      if (!isSectionReady || !experience.current.lenis) {
        return
      }

      if (isMobile) {
        snapRef.current?.destroy()
        snapRef.current = null
      } else {
        const snap = new Snap(experience.current.lenis)
        snapRef.current = snap
      }

      /**
       * Section enter animation
       */
      const enterTriggerEl = selector?.(
        "[data-works-enter]",
      )[0] as HTMLDivElement

      ScrollTrigger.create({
        trigger: enterTriggerEl,
        start: "top bottom",
        end: "top top",
        markers: false,
        scrub: true,
        onEnter() {
          experience.current.trigger(EVENTS.Works.BeforeEnter)
        },
        onLeave() {
          experience.current.trigger(EVENTS.Works.EnteringFinished)
        },
        onEnterBack() {
          experience.current.trigger(EVENTS.Works.EnteringBack)
        },
        onUpdate({ progress, direction }) {
          experience.current.trigger(EVENTS.Works.Entering, [
            {
              progress,
              direction,
            },
          ])
        },
      })

      /**
       * Section exit animation
       * Tracks when the user scrolls past the section
       */
      ScrollTrigger.create({
        trigger: triggerRef.current,
        start: "bottom bottom",
        end: "bottom top",
        markers: false,
        scrub: true,
        onUpdate({ progress }) {
          experience.current.trigger(EVENTS.Works.Exiting, [
            {
              progress,
            },
          ])
        },
      })

      /**
       * Rest of the section
       * this ScrollTrigger wraps the projects thumbs and the last div
       * it is used to animate the camera position
       */
      const restOfSectionTriggerEl =
        enterTriggerEl.nextSibling as HTMLDivElement

      ScrollTrigger.create({
        trigger: restOfSectionTriggerEl,
        start: "top center",
        end: "top top",
        markers: false,
        scrub: true,
        onLeave: () => {
          experience.current.trigger(EVENTS.Works.LeaveIntroTexts)
        },
      })

      ScrollTrigger.create({
        trigger: restOfSectionTriggerEl,
        start: "top center",
        end: "bottom center",
        markers: false,
        scrub: true,
        onUpdate({ progress }) {
          experience.current.trigger(EVENTS.Works.Scroll, [
            {
              progress,
            },
          ])
        },
      })

      /**
       * Project Sections
       */
      const projEls = selector?.("[data-project]")

      if (!isMobile && snapRef.current) {
        for (let i = 0; i < projEls.length; i++) {
          snapRef.current.addElement(projEls[i], {
            align: "center",
          })
        }
      }

      for (let i = 0; i < projEls.length; i++) {
        const trigger = projEls[i]
        ScrollTrigger.create({
          trigger,
          start: "top center",
          end: "bottom center",
          scrub: true,
          // markers: i === 0 ? true : false,
          onEnter: () => {
            if (i === 0) {
              // Trigger the Explosion
              experience.current.trigger(EVENTS.Works.Entered)
            }
            triggerSlideEnter(i)
          },
          onLeaveBack: () => {
            if (i === 0) {
              experience.current.trigger(EVENTS.Works.LeaveWorks)
            } else {
              // Scrolling up: we're entering the previous slide (i - 1)
              triggerSlideEnter(i - 1)
            }
          },
        })
      }

      return () => {
        snapRef.current?.destroy()
        snapRef.current = null
      }
    },
    {
      scope: triggerRef,
      dependencies: [isSectionReady, isMobile],
    },
  )

  return (
    <>
      <div className="fixed top-0 left-0 h-screen w-full flex flex-col justify-start mt-[15vh]">
        <Grid>
          <div className="col-start-1 md:col-start-5 lg:col-start-2 col-span-3">
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="text-gray-700"
              content="_]]"
            />
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="text-gray-700 mt-1"
              content="     [[[WORKS]"
              highlight={{ substring: "WORKS", className: "text-blue-600" }}
            />
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="text-gray-700 mt-1"
              content="  []]"
            />
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="text-gray-700 mt-1"
              content="      ]]["
            />
          </div>
        </Grid>

        <Grid>
          <div className="col-start-2 md:col-start-3 col-span-3">
            <AnimatedText
              data-font-bold
              data-text="intro"
              preventUpdate
              className="mt-[5vh] text-gray-500"
              content={works.description}
            />
          </div>
        </Grid>

        <Grid>
          <div className="col-start-1 md:col-start-5 lg:col-start-2 col-span-3">
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="text-gray-700 mt-4"
              content="   ]]]]_"
            />
          </div>
        </Grid>
      </div>
      <div className="fixed top-0 left-0 h-screen w-full pointer-events-none opacity-0 z-[-1]">
        <Grid className="h-full">
          <div className="hidden lg:flex lg:col-span-5 lg:col-start-2 items-center justify-center">
            <div
              data-project-image
              className="w-full max-w-[500px] aspect-[10/13]"
            />
          </div>
        </Grid>
      </div>

      <div ref={triggerRef}>
        <div data-works-enter className="h-screen" />

        <Grid className="pt-[50vh] lg:pt-[150vh]">
          <div className="col-start-1 col-span-4 md:col-span-8 lg:col-start-3 lg:col-span-8">
            <div
              className="flex flex-col items-stretch justify-center opacity-0"
              data-projects
            >
              {projects.map(({ name, url, videoID, content }) => {
                const industry = content.find((item) => item.industry)?.industry
                const year = content.find((item) => item.year)?.year
                return (
                  <div
                    data-project={url.replace("/", "")}
                    key={name}
                    className="h-[20vh] lg:h-[75vh] flex flex-col items-stretch justify-center"
                  >
                    <div className="px-2 py-4 lg:px-[max(1.5rem,2.25vw)] bg-amber-500/50">
                      <div
                        className="flex justify-between items-center gap-2"
                        data-text-content
                      >
                        <div className="flex-1">
                          <p>{name}</p>
                          <p className="text-gray-600">{industry}</p>
                        </div>
                        <div>
                          <p className="text-right px-1">{year}</p>
                        </div>
                      </div>
                    </div>

                    <video
                      preload="metadata"
                      id={videoID}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="opacity-0 pointer-events-none absolute -z-10"
                    >
                      <source
                        src={`/projects/${videoID}.mp4`}
                        type="video/mp4"
                      />
                    </video>
                  </div>
                )
              })}
            </div>
          </div>
        </Grid>
        <div className="h-[50vh]" />
      </div>
    </>
  )
}

function triggerSlideEnter(index: number) {
  const experience = Experience.getInstance()

  experience.trigger(EVENTS.Works.SlideEnter, [
    {
      index,
    },
  ])
}
